import { db } from '../config/firebase';
import { collection, doc, getDocs, getDoc, query, orderBy } from 'firebase/firestore';
import { PMT2Product } from '../types/pmt2Types';

// Collection names
const SPREADSHEET_CONFIG_COLLECTION = 'pmt2_spreadsheets'; // Needed for getCompanies
const PRODUCT_COLLECTION_BASE = 'pmt2_data';

/**
 * Fetches a list of unique company names from the spreadsheet configurations.
 * @returns Array of company name strings, ordered alphabetically.
 */
export const getCompanies = async (): Promise<string[]> => {
  const configsQuery = query(
    collection(db, SPREADSHEET_CONFIG_COLLECTION),
    orderBy('companyName')
  );
  const snapshot = await getDocs(configsQuery);

  // Use a Set to ensure uniqueness in case of data inconsistencies, though orderBy should handle it
  const companyNames = new Set<string>();
  snapshot.docs.forEach(doc => {
      const companyName = doc.data().companyName;
      if (companyName) {
          companyNames.add(companyName);
      }
  });
  return Array.from(companyNames); // Already sorted by query
};

/**
 * Fetches all products for a given company.
 * @param companyName The name of the company.
 * @returns Array of PMT2Product objects.
 */
export const getProductsByCompany = async (companyName: string): Promise<PMT2Product[]> => {
  if (!companyName) {
      console.warn("getProductsByCompany called with no companyName.");
      return [];
  }
  try {
    const productsRef = collection(db, PRODUCT_COLLECTION_BASE, companyName, 'products');
    const snapshot = await getDocs(productsRef);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PMT2Product));
  } catch (error) {
      console.error(`Error fetching products for company ${companyName}:`, error);
      throw error; // Re-throw error to be handled by caller
  }
};

/**
 * Fetches a single product by its ASIN for a given company.
 * @param companyName The name of the company.
 * @param asin The ASIN of the product.
 * @returns A PMT2Product object or null if not found.
 */
export const getProductByAsin = async (companyName: string, asin: string): Promise<PMT2Product | null> => {
   if (!companyName || !asin) {
      console.warn("getProductByAsin called with no companyName or asin.");
      return null;
  }
   try {
    const docRef = doc(db, PRODUCT_COLLECTION_BASE, companyName, 'products', asin);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as PMT2Product;
    }

    console.log(`Product with ASIN ${asin} not found for company ${companyName}.`);
    return null;
  } catch (error) {
      console.error(`Error fetching product ${asin} for company ${companyName}:`, error);
      throw error; // Re-throw error
  }
};

/**
 * Reverse ASIN lookup - finds which company owns a specific ASIN.
 * @param asin The ASIN to search for across all companies.
 * @returns Object with company name and product data, or null if not found.
 */
export const findProductByAsin = async (asin: string): Promise<{ company: string; product: PMT2Product } | null> => {
  if (!asin) {
    console.warn("findProductByAsin called with no ASIN.");
    return null;
  }

  try {
    // First get all companies
    const companies = await getCompanies();
    
    // Search each company for the ASIN
    for (const companyName of companies) {
      try {
        const product = await getProductByAsin(companyName, asin);
        if (product) {
          return {
            company: companyName,
            product
          };
        }
      } catch (error) {
        // Log error but continue searching other companies
        console.warn(`Error searching for ASIN ${asin} in company ${companyName}:`, error);
        continue;
      }
    }

    // ASIN not found in any company
    return null;
  } catch (error) {
    console.error(`Error in reverse ASIN lookup for ${asin}:`, error);
    throw error;
  }
};